// FlameKingManagerPage.tsx — the Flame King Manager health board.
//
// Structured page (SortableTable). A clean list of health-check snapshots
// written by the Flame King Manager agent, which runs on our Tailormind
// Paperclip and inspects Yogev's box every run. Green dot = healthy, red =
// a problem (which also pings Erez by voice). Click a row for full detail.

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";
import { SortableTable, type ColumnDef } from "../blueprint/SortableTable";

export type HealthSnapshot = {
  id: string;
  status: "ok" | "fail";
  summary: string;
  detail: unknown;
  created_at: string;
};

function StatusDot({ status }: { status: HealthSnapshot["status"] }) {
  const ok = status === "ok";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
      <span style={{
        width: 10, height: 10, borderRadius: 999,
        background: ok ? T.green : T.red,
        boxShadow: `0 0 0 3px ${ok ? T.greenSoft : T.redSoft}`,
      }} />
      <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
        textTransform: "uppercase", color: ok ? T.green : T.red,
      }}>
        {ok ? "OK" : "Problem"}
      </span>
    </span>
  );
}

const COLUMNS: ColumnDef<HealthSnapshot>[] = [
  {
    key: "status", label: "Status", width: "150px",
    sortValue: (s) => s.status,
    render: (s) => <StatusDot status={s.status} />,
  },
  {
    key: "created_at", label: "When", width: "200px",
    render: (s) => <span style={{ color: T.ink3, fontSize: 12 }}>{new Date(s.created_at).toLocaleString()}</span>,
  },
  {
    key: "summary", label: "Summary", sortable: false,
    render: (s) => <span style={{ color: T.ink2 }}>{s.summary}</span>,
  },
];

export function FlameKingManagerPage() {
  const api = useApi();
  const [rows, setRows] = useState<HealthSnapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/flameking-health");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as HealthSnapshot[];
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const latest = rows && rows.length > 0 ? rows[0] : null;

  return (
    <div style={{ fontFamily: "Inter, sans-serif", boxSizing: "border-box", padding: "40px 48px 80px" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ textAlign: "center", padding: "32px 20px 40px", marginBottom: 28 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
            color: T.skyDark, textTransform: "uppercase", marginBottom: 14,
          }}>
            Tailormind · Client health
          </div>
          <h1 style={{
            fontSize: 34, fontWeight: 800, color: T.ink,
            margin: "0 0 14px", letterSpacing: "-0.02em", lineHeight: 1.15,
          }}>
            Flame King Manager
          </h1>
          <p style={{ fontSize: 16, color: T.ink2, lineHeight: 1.65, maxWidth: 720, margin: "0 auto" }}>
            Every run, the Flame King Manager checks Yogev's system — are the routines firing, did the runs succeed, are the Google connectors alive — and writes the result here. Green is healthy; a problem also pings you by voice.
          </p>
          {latest && (
            <div style={{
              marginTop: 20, display: "inline-flex", alignItems: "center", gap: 10,
              padding: "8px 16px", borderRadius: 999, background: T.white, border: `1px solid ${T.line}`,
            }}>
              <span style={{ fontSize: 12, color: T.ink3 }}>Latest:</span>
              <StatusDot status={latest.status} />
              <span style={{ fontSize: 12, color: T.ink3 }}>{new Date(latest.created_at).toLocaleString()}</span>
            </div>
          )}
        </div>

        {error ? (
          <div style={{
            padding: "16px 20px", color: T.red, background: T.redSoft,
            border: `1px solid ${T.red}`, borderRadius: 8, fontSize: 13,
          }}>
            Failed to load: {error}
          </div>
        ) : rows === null ? (
          <div style={{
            padding: 24, fontSize: 14, color: T.ink3,
            display: "inline-flex", alignItems: "center", gap: 8,
          }}>
            <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
            Loading…
          </div>
        ) : (
          <SortableTable
            rows={rows}
            columns={COLUMNS}
            detailHref={(s) => `/flameking-manager/${s.id}`}
            defaultSort={{ key: "created_at", dir: "desc" }}
            emptyMessage="No checks yet — the Manager hasn't run."
          />
        )}
      </div>
    </div>
  );
}

(FlameKingManagerPage as unknown as { path: string }).path = "/flameking-manager";
